import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery'
import { useStyles } from "../styles/styles";
import { getCaseLabel, getCitationLabel } from "./lookupFunctions";
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { NumericFormat } from "react-number-format";
import Tooltip from '@mui/material/Tooltip'
import Zoom from '@mui/material/Zoom'
import OptionList from './OptionList';
import BondList from "./BondList";
import parse from "html-react-parser";
import LineItem from "./LineItem";

const BalanceList = (props) => {
    const balances = props.balances;
    const court = props.court;
    const partialPayment= props.partialPayment;
    const selectBalance = props.selectBalance;
    const payAll = props.payAll;
    const nonCourt = court.nonCourtOrganization;
    const listTitle = props.listTitle;
    const classes = useStyles;
    const theme = useTheme();
    const tiny = useMediaQuery(theme.breakpoints.down('sm'));
    const partialMessage= court.partialMessage
    const priorityBalanceMessage = court.usePriorityBalances && court.priorityBalanceMessage && balances.some(balance => balance.priorityOrder !== null);
    const [payIneligible, setPayIneligible] = useState(null);
    const payAllButtonRef = useRef(null);
    const [trafficSchoolSelections, setTrafficSchoolSelections] = useState({});
    const [tooltipIds, setTooltipIds] = useState({});
    const [balBondId, setBalBondId] = useState(null);

    useEffect(() => {
        if (balances && balances.length > 0) {
            const ids = {};
            balances.forEach(balance => {
                ids[balance.refId] = `tooltip-${balance.refId}`;
            });
            setTooltipIds(ids);
        }
    }, [balances]);

    const handleTrafficSchoolSelect = (balance, optionId) => {
        if (optionId) {
            setPayIneligible(balance.refId);
            balance.bondId = null;
            balance.bondAmt = null;
        }
        setTrafficSchoolSelections(prev => ({ ...prev, [balance.refId]: optionId }));
    };

    const handleBondSelect = (balance, bondId, bondAmt) => {
        if (balance) {
            setBalBondId(bondId);
            if (bondId) {
                balance.bondId = bondId;
                balance.bondAmt = bondAmt;
                setTrafficSchoolSelections(prev => ({ ...prev, [balance.refId]: null }));
                balance.trafficSchoolId = null;
                balance.trafficSchoolType = null;
            } else {
                setPayIneligible(null);
                balance.bondId = null;
                balance.bondAmt = null;
            }
        }
    };

    const handlePleadAndPaySelect = (balance) => {
        setTrafficSchoolSelections(prev => ({ ...prev, [balance.refId]: null }));
        balance.bondId = null;
        balance.bondAmt = null;
        setBalBondId(null);
    };

    return (
        <>
            <Typography variant='h6' component='h2' color='primary' sx={{...classes.title, fontSize: '1.2rem', marginTop: '0.5rem', marginBottom: '.5rem'}}>
                {listTitle ? listTitle : 'Balances'}
            </Typography>
            {partialPayment && partialMessage && (
                <Typography sx={{marginBottom: '1rem', lineHeight: '1.2rem'}}>
                    {parse(partialMessage)}
                </Typography>
            )}
            { priorityBalanceMessage &&
            <Typography color='secondary' sx={{marginBottom: '1rem', lineHeight: '1.2rem'}}>{parse(court.priorityBalanceMessage)}</Typography>
        }
            {balances.filter(balance => !balance.ineligibleForPayment).length > 1 && !court.hidePayAllButton ? (
                <Tooltip title='Add all balances to the payment list' TransitionComponent={Zoom} arrow id="tooltip-pay-all">
                    <Button variant='contained' color='success' onClick={() => payAll(balances)} sx={{ ...classes.tinyButton, mb: '10px', ml: '1.2rem'}} ref={payAllButtonRef} aria-label="Pay all cases" aria-describedby="tooltip-pay-all">
                        Pay All
                    </Button>
                </Tooltip>
            ) : null}
            <Paper elevation={3} sx={{...classes.listContainer, maxHeight: '65vh', overflow: 'auto'}}>
                <List sx={classes.list} >
                    {balances && balances.length > 0 && balances.map((balance, idx) => {
                        return (!balance.selected ? <>
                            <ListItem alignItems='flex-start' sx={classes.listPadding}>
                                <ListItemText
                                    sx={classes.listText}
                                    primary={
                                        <Grid
                                            container
                                            spacing={2}
                                            style={{width: '100%'}}
                                            sx={{
                                                alignItems: "top"
                                            }}
                                        >
                                            <Grid
                                                size={{
                                                    xs: 3,
                                                    sm: 3,
                                                    md: 3,
                                                    lg: 2
                                                }}>
                                                { (!balance.ineligibleForPayment || balance.refId === payIneligible || balance.bondId) ?
                                                    <Tooltip title='Add balance to the payment list' TransitionComponent={Zoom} arrow id={tooltipIds[balance.refId]}>
                                                        { tiny ?
                                                            <Button color="success" onClick={() => selectBalance(balance)} sx={{padding: '0'}} aria-label={`Add to Cart ${getCaseLabel(court)} ${balance.caseNumber}`} aria-describedby={tooltipIds[balance.refId]}>
                                                                Add to Cart
                                                            </Button> :
                                                            <Button variant='contained' color='success' onClick={() => selectBalance(balance)} sx={{...classes.tinyButton, fontSize: {xs: '.65rem', md: '.7rem'}, whiteSpace: 'nowrap', padding: '0 .3rem'}} aria-label={`Add to Cart ${getCaseLabel(court)} ${balance.caseNumber}`} aria-describedby={tooltipIds[balance.refId]}>
                                                                Add to Cart
                                                            </Button>
                                                        }
                                                    </Tooltip> :
                                                    <Typography variant='subtitle2' component='span'>Not Payable</Typography>
                                                }
                                            </Grid>
                                            <Grid
                                                size={{
                                                    xs: 5,
                                                    lg: 3
                                                }}>
                                                {`${getCaseLabel(court)} ${balance.caseNumber}`}
                                            </Grid>
                                            { balance.citationNumber ?
                                                <>
                                                    <Grid
                                                        size={{
                                                            xs: 4,
                                                            lg: 3
                                                        }}>
                                                        {`${getCitationLabel(court)} ${balance.citationNumber}`}
                                                    </Grid>
                                                    <Grid
                                                        size={{
                                                            xs: 10,
                                                            lg: 4
                                                        }}>
                                                        {balance.description}
                                                    </Grid>
                                                </> :
                                                <>
                                                    <Grid
                                                        size={{
                                                            xs: 10,
                                                            lg: 7
                                                        }}>
                                                        {balance.description}
                                                    </Grid>
                                                </>
                                            }
                                        </Grid>
                                    }
                                    primaryTypographyProps={{
                                        component: 'span',
                                        variant: 'subtitle2',
                                        sx: classes.inline,
                                        color: 'textPrimary'
                                    }}
                                    secondary={
                                        <Grid
                                            container
                                            spacing={2}
                                            style={{width: '100%'}}
                                            sx={{
                                                alignItems: "top"
                                            }}
                                        >
                                            <Grid
                                                size={{
                                                    xs: 4,
                                                    lg: 3
                                                }}>
                                                {(!balance.ineligibleForPayment && (!balance.trafficSchoolOptions || balance.trafficSchoolOptions.length === 0) && (!balance.bonds || balance.bonds.length === 0))
                                                    ? `Amount: ${balance.courtAmountDisplay}`
                                                    : ' '}
                                            </Grid>
                                            <Grid
                                                size={{
                                                    xs: balance.disposed ? 8 : 5,
                                                    lg: balance.disposed ? 9 : 6
                                                }}>
                                                {balance.violationDateDisplay ? `${nonCourt ? 'Statement' : 'Violation'} Date: ${balance.violationDateDisplay}`: ' '}
                                            </Grid>
                                            { !balance.disposed &&
                                                <Grid size={3}>
                                                    Pending
                                                </Grid>
                                            }
                                            { !balance.ineligibleForPayment &&
                                                ((court.acceptPartials && !balance.noPartialPayment && balance.disposed) || balance.forceAllowPartialPayment)  &&
                                                <>
                                                    <Grid
                                                        style={{paddingTop: '0'}}
                                                        size={{
                                                            xs: 12,
                                                            lg: 3
                                                        }}>
                                                        <Typography variant='body2' component='label' for={`partialAmt${balance.refId}`}>Partial Payment Amt.</Typography>
                                                    </Grid>
                                                    <Grid
                                                        style={{paddingTop: '0'}}
                                                        size={{
                                                            xs: 12,
                                                            lg: 3
                                                        }}>
                                                        <Tooltip title='Enter a partial payment amount' TransitionComponent={Zoom} arrow>
                                                            <FormControl fullWidth >
                                                                <NumericFormat
                                                                    id={`partialAmt${balance.refId}`}
                                                                    name='partialAmt'
                                                                    customInput={TextField}
                                                                    type='text'
                                                                    autoComplete='off'
                                                                    variant='outlined'
                                                                    style={{marginTop: '0'}}
                                                                    decimalScale={2}
                                                                    decimalSeparator='.'
                                                                    fixedDecimalScale={true}
                                                                    onValueChange={values => {balance.partialAmt = values.floatValue ? values.floatValue : 0.00}}
                                                                    slotProps={{
                                                                        input: {style: {fontSize: '.9rem', height: '1.6rem'}}
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid
                                                        style={{paddingTop: '0'}}
                                                        size={{
                                                            xs: 1,
                                                            lg: 5
                                                        }}>
                                                        <span>&nbsp;</span>
                                                    </Grid>
                                                </>
                                            }
                                            { !balance.ineligibleForPayment && balance.lineItems && balance.lineItems.length > 0 &&
                                                <>
                                                    <Grid
                                                        style={{paddingTop: '0'}}
                                                        size={{
                                                            xs: 0,
                                                            lg: 2
                                                        }}>&nbsp;</Grid>
                                                    <Grid
                                                        style={{paddingTop: '0'}}
                                                        size={{
                                                            xs: 12,
                                                            lg: 6
                                                        }}>
                                                        <LineItem balance={balance} />
                                                    </Grid>
                                                    <Grid
                                                        style={{paddingTop: '0'}}
                                                        size={{
                                                            xs: 0,
                                                            lg: 4
                                                        }}>
                                                        <span>&nbsp;</span>
                                                    </Grid>
                                                </>
                                            }
                                            { !balance.ineligibleForPayment && (balance.trafficSchoolOptions && balance.trafficSchoolOptions.length > 0 || balance.bonds && balance.bonds.length > 0) &&
                                                <List sx={{...classes.list, maxHeight: '100%', marginLeft: '1rem'}} >
                                                    <ListItem alignItems='flex-start' sx={classes.listPadding}>
                                                        <ListItemText
                                                            sx={classes.listText}
                                                            primary={
                                                                <Grid container spacing={1} sx={{
                                                                    alignItems: "top"
                                                                }}>
                                                                    <Grid size={3}>&nbsp;</Grid>
                                                                    <Grid sx={{fontWeight: 'bold', fontSize: '0.9rem'}} size={6}>Description</Grid>
                                                                    <Grid
                                                                        sx={{paddingLeft: '1rem', fontWeight: 'bold', fontSize: '0.9rem'}}
                                                                        size={3}>Amount</Grid>
                                                                </Grid>
                                                            }
                                                        />
                                                    </ListItem>
                                                    <ListItem alignItems='flex-start' disableGutters sx={{paddingTop: '0', paddingBottom: '0'}}>
                                                        <ListItemText
                                                            sx={classes.listText}
                                                            primary={
                                                                <Grid container spacing={1} sx={{
                                                                    alignItems: "top"
                                                                }}>
                                                                    <Grid size={3}>
                                                                        <FormControlLabel
                                                                            control={<Radio size='small' checked={!trafficSchoolSelections[balance.refId] && !balance.bondId} onChange={() => handlePleadAndPaySelect(balance)} sx={{marginLeft: '1rem', paddingBottom: '0', paddingLeft: '.5rem', paddingTop: '0'}} slotProps={{
                                                                                input: {'aria-label': `Select plead and pay option for ${getCaseLabel(court)} ${balance.caseNumber}`}
                                                                            }} />}
                                                                            label=''
                                                                            aria-label='Plead and pay in full only'
                                                                            sx={{paddingLeft: '0', paddingTop: '0'}}
                                                                        />
                                                                    </Grid>
                                                                    <Grid size={6}>Plead and pay in full only</Grid>
                                                                    <Grid
                                                                        sx={{textAlign: 'right', paddingRight: {xs: '5%', sm: '14%', lg: '21%'}}}
                                                                        size={3}>
                                                                        {balance.courtAmountDisplay}
                                                                    </Grid>
                                                                </Grid>
                                                            }
                                                            primaryTypographyProps={{
                                                                component: 'span',
                                                                color: 'textPrimary',
                                                                sx: {fontSize: '.9rem', paddingLeft: '.2rem'}
                                                            }}
                                                        />
                                                    </ListItem>
                                                </List>
                                            }
                                            { (balance.trafficSchoolOptions?.length > 0 && (!balance.ineligibleForPayment || (court.bondOnlyBalances && balance.bonds && balance.bonds.length > 0))) &&
                                                <OptionList
                                                    options={balance.trafficSchoolOptions}
                                                    balance={balance}
                                                    setPayIneligible={setPayIneligible}
                                                    onSelect={optionId => handleTrafficSchoolSelect(balance, optionId)} selectedOption={trafficSchoolSelections[balance.refId] || null}
                                                    court={court}
                                                />
                                            }
                                            { ((court.bondOnlyBalances && balance.ineligibleForPayment) || (!balance.ineligibleForPayment)) && balance.bonds && balance.bonds.length > 0 &&
                                                <BondList bonds={balance.bonds} balance={balance} setPayIneligible={setPayIneligible} onSelect={handleBondSelect} trafficSchoolSelections={trafficSchoolSelections} balBondId={balBondId}/>
                                            }
                                            { balance.balanceDisplayMessage &&
                                                <Grid style={{paddingTop: '0'}} size={12}>
                                                    <Typography color='secondary'>
                                                        {balance.balanceDisplayMessage}
                                                    </Typography>
                                                </Grid>
                                            }
                                        </Grid>
                                    }
                                    secondaryTypographyProps={{
                                        component: 'span',
                                        variant: 'body2',
                                        sx: classes.inline,
                                        color: 'textPrimary'
                                    }}
                                />
                            </ListItem>
                            { idx < (balances.length - 1) &&
                                <Divider sx={classes.listDivider}/>
                            }
                        </> : null);
                    })}
                </List>
            </Paper>
        </>
    );
}

export default BalanceList;
